import { getMessage } from '@tcc/i18n';
import { CategoryStore, ScoutingState } from '../actions/scouting/types/state';
import {
  REQUEST_NEW_ACTIVITY,
  RECEIVE_NEW_ACTIVITY,
  FAILURE_NEW_ACTIVITY,
  REQUEST_DELETE_ACTIVITY,
  RECEIVE_DELETE_ACTIVITY,
  FAILURE_DELETE_ACTIVITY,
  REQUEST_ACTIVITIES,
  RECEIVE_ACTIVITIES,
  FAILURE_ACTIVITIES,
  SET_SELECTED_ACTIVITY,
  REQUEST_TAGS,
  RECEIVE_TAGS,
  FAILURE_TAGS,
  REQUEST_CATEGORY,
  RECEIVE_CATEGORY,
  FAILURE_CATEGORY,
  RECEIVE_UPDATE_ACTIVITY,
  REQUEST_UPDATE_ACTIVITY,
  FAILURE_UPDATE_ACTIVITY,
  REQUEST_UPDATE_ENHANCED_ACTIVITY,
  FAILURE_UPDATE_ENHANCED_ACTIVITY,
  REQUEST_DELETE_ASSET,
  RECEIVE_DELETE_ASSET,
  FAILURE_DELETE_ASSET,
  START_DELETE_COMMENTS,
  RECEIVE_DELETE_COMMENTS,
  REQUEST_ACTIVITY,
  EDIT_ACTIVITY,
  FAILURE_ACTIVITY,
} from '../constants/actionTypes';
import { getActivitySeasonYear } from '../utils/scoutingUtils';
import { AsyncStatus, CategoryResponse } from '../types';

export const initialState: ScoutingState = {
  isLoadingActivity: false,
  isLoadingActivities: false,
  activities: {},
  selectedActivity: [null, null],
  isLoadingTags: false,
  tags: [],
  isLoadingUpdatedActivity: false,
  isLoadingAsset: false,
  categories: {},
  categoriesStatus: AsyncStatus.IDLE,
};

type ActionMap = Record<
  string, // eslint-disable-next-line
  (state: ScoutingState, action: Record<string, any>) => ScoutingState
>;

const actionMap: ActionMap = {
  [REQUEST_NEW_ACTIVITY](state) {
    return {
      ...state,
      isLoadingActivity: true,
    };
  },
  [RECEIVE_NEW_ACTIVITY](state, action) {
    const activityYear = getActivitySeasonYear(action.payload, action.payload.scoutedAt);
    const isNewYear = Boolean(state.activities[activityYear]);

    return {
      ...state,
      isLoadingActivity: false,
      activities: {
        ...state.activities,
        [activityYear]: isNewYear
          ? [action.payload, ...state.activities[activityYear]]
          : [action.payload],
      },
    };
  },
  [FAILURE_NEW_ACTIVITY](state) {
    return {
      ...state,
      isLoadingActivity: false,
    };
  },
  [REQUEST_DELETE_ACTIVITY](state) {
    return {
      ...state,
      isLoadingActivity: true,
    };
  },
  [RECEIVE_DELETE_ACTIVITY](state, action) {
    const activityYear = getActivitySeasonYear(action.payload, action.payload.scoutedAt);

    return {
      ...state,
      isLoadingActivity: false,
      activities: {
        ...state.activities,
        [activityYear]: state.activities[activityYear].filter(
          (activity) => activity.id !== action.payload.id
        ),
      },
    };
  },
  [REQUEST_UPDATE_ACTIVITY](state) {
    return {
      ...state,
      isLoadingUpdatedActivity: true,
    };
  },
  [FAILURE_UPDATE_ACTIVITY](state) {
    return {
      ...state,
      isLoadingUpdatedActivity: false,
    };
  },
  [RECEIVE_UPDATE_ACTIVITY](state, action) {
    const {
      activity,
      permanent: { from, to },
    } = action.payload;
    if (to !== from) {
      const isNewYear = Boolean(state.activities[to]);
      return {
        ...state,
        isLoadingUpdatedActivity: false,
        activities: {
          ...state.activities,
          [from]: state.activities[from].filter(
            (act) => act.id !== activity.id
          ),
          [to]: isNewYear ? [activity, ...state.activities[to]] : [activity],
        },
      };
    }

    return {
      ...state,
      isLoadingUpdatedActivity: false,
      activities: {
        ...state.activities,
        [to]: state.activities[to].map((act) => {
          if (act.id === activity.id) {
            return activity;
          }

          return act;
        }),
      },
    };
  },
  [REQUEST_UPDATE_ENHANCED_ACTIVITY](state) {
    return {
      ...state,
      isLoadingUpdatedActivity: true,
    };
  },
  [FAILURE_UPDATE_ENHANCED_ACTIVITY](state) {
    return {
      ...state,
      isLoadingUpdatedActivity: false,
    };
  },
  [FAILURE_DELETE_ACTIVITY](state) {
    return {
      ...state,
      isLoadingActivity: false,
    };
  },
  [REQUEST_ACTIVITIES](state) {
    return {
      ...state,
      isLoadingActivities: true,
    };
  },
  [RECEIVE_ACTIVITIES](state, action) {
    return {
      ...state,
      isLoadingActivities: false,
      activities: action.payload,
    };
  },
  [FAILURE_ACTIVITIES](state) {
    return {
      ...state,
      isLoadingActivities: false,
    };
  },
  [SET_SELECTED_ACTIVITY](state, action) {
    return {
      ...state,
      selectedActivity: state.selectedActivity.map((value, index) =>
        index === action.index ? action.id : value
      ),
    };
  },
  [REQUEST_TAGS](state) {
    return {
      ...state,
      isLoadingTags: true,
    };
  },
  [RECEIVE_TAGS](state, action) {
    const tags = action.payload.data.map((tag) => ({
      ...tag,
      value: getMessage(`field.note.tag.${tag.text}`) || tag.text,
    }));

    return {
      ...state,
      isLoadingTags: false,
      tags,
    };
  },
  [FAILURE_TAGS](state) {
    return {
      ...state,
      isLoadingTags: false,
    };
  },
  [REQUEST_DELETE_ASSET](state) {
    return {
      ...state,
      isLoadingAsset: true,
    };
  },
  [RECEIVE_DELETE_ASSET](state) {
    return {
      ...state,
      isLoadingAsset: false,
    };
  },
  [FAILURE_DELETE_ASSET](state) {
    return {
      ...state,
      isLoadingAsset: false,
    };
  },
  [START_DELETE_COMMENTS](state) {
    return {
      ...state,
      isLoadingActivity: true
    };
  },
  [RECEIVE_DELETE_COMMENTS](state) {
    return {
      ...state,
      isLoadingActivity: false
    };
  },
  [REQUEST_ACTIVITY](state) {
    return {
      ...state,
      isLoadingActivity: true
    };
  },
  [EDIT_ACTIVITY](state, action) {
    const { activity } = action.payload;

    const activityYear = getActivitySeasonYear(activity, activity.scoutedAt)

    const { activities } = state;

    const newSeason = activities[activityYear].map(_activity => {
      if (_activity.id === activity.id) {
        return activity;
      }

      return _activity;
    });

    const newActivities = {
      ...activities,
      [activityYear]: newSeason
    };

    return {
      ...state,
      isLoadingActivity: false,
      activities: newActivities
    };
  },
  [FAILURE_ACTIVITY](state) {
    return {
      ...state,
      isLoadingActivity: false,
    };
  },
  [REQUEST_CATEGORY](state) {
    return {
      ...state,
      categoriesStatus: AsyncStatus.PENDING,
    };
  },
  [RECEIVE_CATEGORY](state, action) {
    const categories = action.payload.data as CategoryResponse[];

    const categoriesMap = categories.reduce<CategoryStore>((categoryMap, { id, categoryType, nameCode, levelCode, order }) => {
      if (categoryMap[categoryType] === undefined) {
        categoryMap[categoryType] = [];
      }

      const index = categoryMap[categoryType].findIndex(el => el.attribute === nameCode);

      if (index !== -1) {
        categoryMap[categoryType][index].levels.push({ id, level: levelCode, order });
      } else {
        categoryMap[categoryType].push({
          attribute: nameCode,
          levels: [{ id, level: levelCode, order }],
        });
      }

      return categoryMap;
    }, {} as CategoryStore);

    return {
      ...state,
      categoriesStatus: AsyncStatus.RESOLVED,
      categories: categoriesMap
    };
  },
  [FAILURE_CATEGORY](state) {
    return {
      ...state,
      categoriesStatus: AsyncStatus.REJECTED,
    };
  },
};

/**
 * Handles redux state for scouting
 * @param {Object} state
 * @param {Object} action
 * @return {Object} new state
 */
function scoutingReducer(state: ScoutingState = initialState, action) {
  if (actionMap[action.type]) {
    return actionMap[action.type](state, action);
  }

  return state;
}

export default scoutingReducer;
